/**
 * Route Configuration
 * Centralized route definitions for the application
 */

import React from 'react';
import { Navigate } from 'react-router-dom';

// Lazy load pages for better performance
const HomePageNew = React.lazy(() => import('../pages/HomePageNew'));
const MasterVoyagePage = React.lazy(() => import('../pages/MasterVoyagePage'));
const BoardingDeck = React.lazy(() => import('../pages/BoardingDeck'));
const SelahChamber = React.lazy(() => import('../pages/SelahChamber'));
const LibraryPage = React.lazy(() => import('../pages/LibraryPage'));
const ShowroomPage = React.lazy(() => import('../pages/ShowroomPage'));
const FellowshipPage = React.lazy(() => import('../pages/FellowshipPage'));
const ProductsPage = React.lazy(() => import('../pages/ProductsPage'));
const PetsPage = React.lazy(() => import('../pages/PetsPage'));
const CollectionsPage = React.lazy(() => import('../pages/CollectionsPage'));
const BrandLinePage = React.lazy(() => import('../pages/BrandLinePage'));
const PhotoGiftsPage = React.lazy(() => import('../pages/PhotoGiftsPage'));
const ProductDetailPage = React.lazy(() => import('../pages/ProductDetailPage'));
const CartPage = React.lazy(() => import('../pages/CartPage'));
const CheckoutPage = React.lazy(() => import('../pages/CheckoutPage'));
const GiftFinderPage = React.lazy(() => import('../pages/GiftFinderPage'));
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const RegisterPage = React.lazy(() => import('../pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('../pages/ForgotPasswordPage'));
const OrdersPage = React.lazy(() => import('../pages/OrdersPage'));
const OrderDetailPage = React.lazy(() => import('../pages/OrderDetailPage'));
const PoliciesPage = React.lazy(() => import('../pages/PoliciesPage'));
const OrderTrackingPage = React.lazy(() => import('../pages/OrderTrackingPage'));
const BlogPage = React.lazy(() => import('../pages/BlogPage'));
const BlogDetailPage = React.lazy(() => import('../pages/BlogDetailPage'));
const AffiliatePage = React.lazy(() => import('../pages/AffiliatePage'));
const PartnerSuccessPage = React.lazy(() => import('../pages/PartnerSuccessPage'));
const AffiliateContentLibrary = React.lazy(() => import('../pages/AffiliateContentLibrary'));
const LaunchLandingPage = React.lazy(() => import('../pages/LaunchLandingPage'));
const InfluencerOutreachKit = React.lazy(() => import('../pages/InfluencerOutreachKit'));
const HeadToToeCatalog = React.lazy(() => import('../pages/HeadToToeCatalog'));
const PrivacyPolicyPage = React.lazy(() => import('../pages/PrivacyPolicyPage'));
const SizingChartPage = React.lazy(() => import('../pages/SizingChartPage'));
const CrewProfilesPage = React.lazy(() => import('../pages/CrewProfilesPage'));
const ShippingPage = React.lazy(() => import('../pages/ShippingPage'));
const ReturnsPage = React.lazy(() => import('../pages/ReturnsPage'));
const LeadMagnetPage = React.lazy(() => import('../pages/LeadMagnetPage'));
const AboutPage = React.lazy(() => import('../pages/AboutPage'));
const AccountSettings = React.lazy(() => import('../pages/AccountSettings'));
const ImmersiveShipStore = React.lazy(() => import('../pages/ImmersiveShipStore'));
import { WishlistPage } from '../components/Wishlist';

// Admin Pages
const AdminDashboard = React.lazy(() => import('../pages/admin/AdminDashboard'));
const AdminProducts = React.lazy(() => import('../pages/admin/AdminProducts'));
const AdminOrders = React.lazy(() => import('../pages/admin/AdminOrders'));
const AdminFulfillment = React.lazy(() => import('../pages/admin/AdminFulfillment'));
const PackingSlipPage = React.lazy(() => import('../pages/admin/PackingSlipPage'));
const AdminAutopilot = React.lazy(() => import('../pages/admin/AdminAutopilot'));
const AdminSuppliers = React.lazy(() => import('../pages/admin/AdminSuppliers'));
const AdminInventory = React.lazy(() => import('../pages/admin/AdminInventory'));
const AdminIntegrations = React.lazy(() => import('../pages/admin/AdminIntegrations'));
const AdminProductImport = React.lazy(() => import('../pages/admin/AdminProductImport'));
const AdminMarketing = React.lazy(() => import('../pages/admin/AdminMarketing'));
const AdminCoach = React.lazy(() => import('../pages/admin/AdminCoach'));
const LaunchChecklist = React.lazy(() => import('../pages/admin/LaunchChecklist'));
const ContentTemplates = React.lazy(() => import('../pages/admin/ContentTemplates'));
const AdminAisha = React.lazy(() => import('../pages/admin/AdminAisha'));
const AishaPricing = React.lazy(() => import('../pages/admin/AishaPricing'));
const AishaSubscriptionSuccess = React.lazy(() => import('../pages/admin/AishaSubscriptionSuccess'));
const AishaSmartSearch = React.lazy(() => import('../pages/admin/AishaSmartSearch'));
const AishaRedditResearch = React.lazy(() => import('../pages/admin/AishaRedditResearch'));
const AishaAutomation = React.lazy(() => import('../pages/admin/AishaAutomation'));
const AishaPlatforms = React.lazy(() => import('../pages/admin/AishaPlatforms'));
const CrewManifest = React.lazy(() => import('../pages/admin/CrewManifest'));
const EmailDeals = React.lazy(() => import('../pages/admin/EmailDeals'));
const CommandBridge = React.lazy(() => import('../pages/admin/CommandBridge'));
const IntegrityDashboard = React.lazy(() => import('../pages/admin/IntegrityDashboard'));
const ProspectBoard = React.lazy(() => import('../pages/admin/ProspectBoard'));
const AishaEmailList = React.lazy(() => import('../pages/admin/AishaEmailList'));
const AishaSyndication = React.lazy(() => import('../pages/admin/AishaSyndication'));
const AishaAdBudget = React.lazy(() => import('../pages/admin/AishaAdBudget'));
const AishaMarketIntel = React.lazy(() => import('../pages/admin/AishaMarketIntel'));
const AishaTolerance = React.lazy(() => import('../pages/admin/AishaTolerance'));
const AishaPromotions = React.lazy(() => import('../pages/admin/AishaPromotions'));
const AishaOrganicGrowth = React.lazy(() => import('../pages/admin/AishaOrganicGrowth'));
const AishaTrafficFunnel = React.lazy(() => import('../pages/admin/AishaTrafficFunnel'));
const AishaSocialTemplates = React.lazy(() => import('../pages/admin/AishaSocialTemplates'));
const AishaAnalytics = React.lazy(() => import('../pages/admin/AishaAnalytics'));
const AishaPostScheduler = React.lazy(() => import('../pages/admin/AishaPostScheduler'));
const AishaCartRecovery = React.lazy(() => import('../pages/admin/AishaCartRecovery'));
const AishaChiefExecutive = React.lazy(() => import('../pages/admin/AishaChiefExecutive'));
const VendorManagement = React.lazy(() => import('../pages/admin/VendorManagement'));
const DisputeDashboard = React.lazy(() => import('../pages/admin/DisputeDashboard'));

/**
 * Public store routes - no authentication required
 */
export const publicRoutes = [
  { path: '/', element: BoardingDeck, layout: null },
  { path: '/home', element: HomePageNew, layout: null },
  { path: '/admin/packing-slip/:orderId', element: PackingSlipPage, layout: null },
  { path: '/ship', element: ImmersiveShipStore, layout: null },
  { path: '/voyage', element: MasterVoyagePage, layout: null },
  { path: '/selah', element: SelahChamber, layout: null },
  { path: '/library', element: LibraryPage, layout: null },
  { path: '/showroom', element: ShowroomPage, layout: null },
  { path: '/fellowship', element: FellowshipPage, layout: null },
  { path: '/products', element: ProductsPage, layout: 'store' },
  { path: '/pets', element: PetsPage, layout: 'store' },
  { path: '/products/:id', element: ProductDetailPage, layout: 'store' },
  { path: '/collections', element: CollectionsPage, layout: 'store' },
  { path: '/collections/:line', element: BrandLinePage, layout: 'store' },
  { path: '/photo-gifts', element: PhotoGiftsPage, layout: 'store' },
  { path: '/catalog', element: HeadToToeCatalog, layout: 'store' },
  { path: '/cart', element: CartPage, layout: 'store' },
  { path: '/checkout', element: CheckoutPage, layout: 'store' },
  { path: '/gift-finder', element: GiftFinderPage, layout: 'store' },
  { path: '/checkout/success', element: CheckoutPage, layout: 'store' },
  { path: '/orders', element: OrdersPage, layout: 'store' },
  { path: '/orders/:orderId', element: OrderDetailPage, layout: 'store' },
  { path: '/policies', element: PoliciesPage, layout: 'store' },
  { path: '/track', element: OrderTrackingPage, layout: 'store' },
  { path: '/blog', element: BlogPage, layout: 'store' },
  { path: '/blog/:slug', element: BlogDetailPage, layout: 'store' },
  { path: '/wishlist', element: WishlistPage, layout: 'store', isComponent: true },
  { path: '/affiliate', element: AffiliatePage, layout: 'store' },
  { path: '/partner/success', element: PartnerSuccessPage, layout: null },
  { path: '/affiliate/content', element: AffiliateContentLibrary, layout: null },
  { path: '/launch', element: LaunchLandingPage, layout: null },
  { path: '/influencer-kit', element: InfluencerOutreachKit, layout: null },
  { path: '/free-faith-guide', element: LeadMagnetPage, layout: null },
  // Privacy policy aliases
  { path: '/policy', element: PrivacyPolicyPage, layout: null },
  { path: '/privacy', element: PrivacyPolicyPage, layout: null },
  { path: '/privacy-policy', element: PrivacyPolicyPage, layout: null },
  // About aliases  
  { path: '/about', element: AboutPage, layout: 'store' },
  { path: '/about-us', element: AboutPage, layout: 'store' },
  { path: '/trust', element: AboutPage, layout: 'store' },
  // Sizing aliases
  { path: '/sizing', element: SizingChartPage, layout: 'store' },
  { path: '/sizing-chart', element: SizingChartPage, layout: 'store' },
  { path: '/size-guide', element: SizingChartPage, layout: 'store' },
  // Crew/team aliases
  { path: '/crew', element: CrewProfilesPage, layout: null },
  { path: '/team', element: CrewProfilesPage, layout: null },
  { path: '/about/crew', element: CrewProfilesPage, layout: null },
  // Info pages
  { path: '/shipping', element: ShippingPage, layout: 'store' },
  { path: '/returns', element: ReturnsPage, layout: 'store' },
  // Auth pages
  { path: '/login', element: LoginPage, layout: 'store' },
  { path: '/register', element: RegisterPage, layout: 'store' },
  { path: '/forgot-password', element: ForgotPasswordPage, layout: 'store' },
];

/**
 * Protected routes - requires authentication
 */
export const protectedRoutes = [
  { path: '/account', element: AccountSettings, layout: null },
  { path: '/account/settings', element: AccountSettings, layout: null },
  { path: '/settings', element: AccountSettings, layout: null },
];

/**
 * Admin routes - requires admin authentication
 */
export const adminRoutes = [
  { path: '/admin', element: AdminDashboard },
  { path: '/admin/products', element: AdminProducts },
  { path: '/admin/orders', element: AdminOrders },
  { path: '/admin/fulfillment', element: AdminFulfillment },
  { path: '/admin/autopilot', element: AdminAutopilot },
  { path: '/admin/suppliers', element: AdminSuppliers },
  { path: '/admin/inventory', element: AdminInventory },
  { path: '/admin/integrations', element: AdminIntegrations },
  { path: '/admin/import', element: AdminProductImport },
  { path: '/admin/marketing', element: AdminMarketing },
  { path: '/admin/coach', element: AdminCoach },
  { path: '/admin/launch', element: LaunchChecklist },
  { path: '/admin/content', element: ContentTemplates },
  { path: '/admin/aisha', element: AdminAisha },
  { path: '/admin/aisha/platforms', element: AishaPlatforms },
  { path: '/admin/aisha/crew', element: CrewManifest },
  { path: '/admin/aisha/email-deals', element: EmailDeals },
  { path: '/admin/aisha/command', element: CommandBridge },
  { path: '/admin/integrity', element: IntegrityDashboard },
  { path: '/admin/aisha/prospects', element: ProspectBoard },
  { path: '/admin/aisha/smartsearch', element: AishaSmartSearch },
  { path: '/admin/aisha/reddit', element: AishaRedditResearch },
  { path: '/admin/aisha/automation', element: AishaAutomation },
  { path: '/admin/aisha/email-list', element: AishaEmailList },
  { path: '/admin/aisha/syndication', element: AishaSyndication },
  { path: '/admin/aisha/ad-budget', element: AishaAdBudget },
  { path: '/admin/aisha/market-intel', element: AishaMarketIntel },
  { path: '/admin/aisha/tolerance', element: AishaTolerance },
  { path: '/admin/aisha/promotions', element: AishaPromotions },
  { path: '/admin/aisha/organic-growth', element: AishaOrganicGrowth },
  { path: '/admin/aisha/traffic-funnel', element: AishaTrafficFunnel },
  { path: '/admin/aisha/social-templates', element: AishaSocialTemplates },
  { path: '/admin/aisha/analytics', element: AishaAnalytics },
  { path: '/admin/aisha/scheduler', element: AishaPostScheduler },
  { path: '/admin/aisha/cart-recovery', element: AishaCartRecovery },
  { path: '/admin/aisha/chief-executive', element: AishaChiefExecutive },
  { path: '/admin/vendors', element: VendorManagement },
  { path: '/admin/disputes', element: DisputeDashboard },
];

/**
 * Admin routes without admin layout (special pages)
 */
export const adminSpecialRoutes = [
  { path: '/admin/aisha/pricing', element: AishaPricing },
  { path: '/admin/aisha/subscription-success', element: AishaSubscriptionSuccess },
];

/**
 * Fallback route
 */
export const fallbackRoute = { path: '*', element: () => <Navigate to="/" replace /> };
